import { Plugin, PluginKey, TextSelection } from 'prosemirror-state'
import { search as EmojiSearch, get as EmojiGet } from 'node-emoji'

import Styles from './EmojiInput.module.scss'
import EmojiInputView from './EmojiInputView'
import { type EditorView } from 'prosemirror-view'
import { getCharBeforeCursor, getCharBeforePosition } from 'common/Prose/utils/getCharBeforeHelpers'
import { createRoot, Root } from 'react-dom/client'

const TOP_PADDING = 25
const LEFT_PADDING = 10
const TRIGGER_CHARACTER = ':'
const MIN_QUERY_LENGTH = 2

const defaultState = {
  active: false,
  query: '',
  triggerPos: 0,
  results: [],
}

export const EmojiInputKey = new PluginKey('emojiInput')

const replaceWithEmoji = (emoji: string, view: EditorView) => {
  const { state } = view

  // Select and replace trigger character and query with emoji
  const pluginState = EmojiInputKey.getState(state)
  const anchorRes = state.doc.resolve(pluginState.triggerPos - 1)
  const headRes = state.doc.resolve(anchorRes.pos + pluginState.query.length + 1)
  const selection = new TextSelection(anchorRes, headRes)
  const tx = view.state.tr.setSelection(selection)
  tx.insertText(emoji)

  view.dispatch(tx)
  view.focus()
}

const EmojiInputPlugin = new Plugin({
  key: EmojiInputKey,

  props: {
    handleDOMEvents: {
      keydown: (view, event) => {
        const pluginState = EmojiInputKey.getState(view.state)
        if (pluginState.active && pluginState.results.length && pluginState.query.length >= MIN_QUERY_LENGTH) {
          return handleKeyDown(event as KeyboardEvent, view)
        }
        return false
      },
    },
  },

  view(editorView) {
    let containerDom: HTMLDivElement
    let root: Root
    let currentPosition: { left: number; top: number } = { left: 0, top: 0 }

    return {
      update: (view, prevState) => {
        if (!this.key) return
        const prevPluginState = this.key.getState(prevState)
        const newPluginState = this.key.getState(view.state)

        if (!prevPluginState.active && newPluginState.active) {
          const position = editorView.coordsAtPos(editorView.state.selection.anchor)
          containerDom = document.createElement('div')
          containerDom.className = Styles.container
          root = createRoot(containerDom)
          root.render(
            <EmojiInputView
              results={newPluginState.results}
              onEmojiSelect={(emoji) => replaceWithEmoji(emoji, view)}
            />,
          )

          if (view.dom.parentNode) {
            view.dom.parentNode.appendChild(containerDom)
          }

          if (containerDom.offsetParent) {
            const { parent } = view.state.selection.$anchor
            let extraTopPadding = 0

            if (parent) {
              extraTopPadding = parent.type.spec.bottomMenuPadding || 0
            }

            const box = containerDom.offsetParent.getBoundingClientRect()
            currentPosition = {
              top: position.top - box.top + extraTopPadding + TOP_PADDING,
              left: position.left - box.left - LEFT_PADDING,
            }
            containerDom.style.top = currentPosition.top + 'px'
            containerDom.style.left = currentPosition.left + 'px'
          }
        }

        if (prevPluginState.active) {
          if (newPluginState.active) {
            // Still active, update results
            root.render(
              <EmojiInputView
                results={newPluginState.results}
                onEmojiSelect={(emoji) => replaceWithEmoji(emoji, view)}
              />,
            )
          } else {
            // Deactivated, remove and clean up
            root.unmount()
            containerDom.remove()
          }
        }
      },
    }
  },

  state: {
    init() {
      return defaultState
    },

    apply(tr, prevState, old, updated) {
      const next = { ...prevState }

      if (prevState.active) {
        if (!updated.selection.empty || Math.abs(old.selection.anchor - updated.selection.anchor) > 1) {
          // Selection was clicked somewhere else
          return defaultState
        }

        if (getCharBeforePosition(updated, prevState.triggerPos) !== TRIGGER_CHARACTER) {
          // Trigger character deleted
          return defaultState
        }

        next.query = updated.doc.textBetween(prevState.triggerPos, updated.selection.anchor)

        if (next.query.length < MIN_QUERY_LENGTH) {
          next.results = []
        } else {
          next.results = EmojiSearch(next.query)
        }

        if (next.query.endsWith(' ') || next.query.endsWith(TRIGGER_CHARACTER) || next.query === ' ') {
          return defaultState
        }
      }

      if (tr.docChanged && updated.selection.empty) {
        if (!prevState.active && getCharBeforeCursor(updated) === TRIGGER_CHARACTER) {
          next.triggerPos = updated.selection.anchor
          next.active = true
        }
      }

      return next
    },
  },
})

const handleKeyDown = (event: KeyboardEvent, view: EditorView) => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
    event.preventDefault()
    return true
  }

  if (event.key === TRIGGER_CHARACTER) {
    const pluginState = EmojiInputKey.getState(view.state)
    const queriedEmoji = EmojiGet(pluginState.query)
    if (queriedEmoji) {
      event.preventDefault()
      replaceWithEmoji(queriedEmoji, view)
      return true
    }
  }

  return false
}

export default EmojiInputPlugin
